/** Any callable; a result is never read through this type. */
export type AnyFunction = (...args: never[]) => void;

/** The members of a union `T` that are `U`. Callers narrowing an `unknown` value use a dedicated predicate instead. */
export type Members<T, U> = Extract<T, U>;

export function isString<T>(value: T): value is T & string {
  return typeof value === 'string';
}

export function isNumber<T>(value: T): value is T & number {
  return typeof value === 'number';
}

export function isBoolean<T>(value: T): value is T & boolean {
  return typeof value === 'boolean';
}

export function isFunction<T>(value: T): value is Members<T, AnyFunction> {
  return typeof value === 'function';
}

export function isList<T>(value: T): value is Members<T, readonly unknown[]> {
  return Array.isArray(value);
}

export function isObjectLike<T>(value: T): value is T & object {
  return typeof value === 'object' && value !== null;
}

export function isRecord<T>(value: T): value is T & object {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isDefined<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

/**
 * Whether the environment provides `name`: a browser API on the server, or in
 * a test window, may be absent or be a key holding `undefined` (happy-dom's
 * `WebSocket`), and only a value that is there counts.
 */
export function hasGlobal(name: keyof typeof globalThis): boolean {
  return globalThis[name] !== undefined;
}
